import { useEffect, useState } from 'react';

/**
 * Matches a CSS media query reactively. SSR- and jsdom-safe: returns `false`
 * when `matchMedia` is unavailable, so tests get the middle (tablet) layout
 * unless they explicitly stub `window.matchMedia`.
 */
export function useMediaQuery(query: string): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
  const [matches, setMatches] = useState(() =>
    supported ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    if (!supported) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [supported, query]);

  return matches;
}

/**
 * Booking-detail responsive tiers (Tailwind breakpoints):
 *   - `isPhone`   — below `md` (768px): single column + fixed bottom action bar.
 *   - `isDesktop` — at/above `lg` (1024px): inline header code+status (no status
 *     band). The middle band (tablet) gets the two-column rail + the status band.
 */
export function useViewport(): { isPhone: boolean; isDesktop: boolean } {
  const isPhone = useMediaQuery('(max-width: 767px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  return { isPhone, isDesktop };
}
